import React, { FC } from "react";
import Button from "components/Button/Button";
import CopyToClipboard from "components/CopyToClipboard/CopyToClipboard";
import { ButtonColor, ButtonVariant } from "enums/Button";
import { IconType } from "enums/Icon";
import { List } from "immutable";
import { CopyToClipboard as CopyToClipboardLib } from "react-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createStructuredSelector } from "reselect";
import useFetchOnceEffect from "../../../../hooks/useFetchOnceEffect";
import { getMailboxes } from "../../../../reduxFolder/reducers/mailbox";
import {
  authSelectFfid,
  authSelectFullName,
} from "../../../../reduxFolder/selectors/globalSelectors";
import {
  mailboxSelectLoading,
  mailboxSelectMailboxes,
} from "../../../../reduxFolder/selectors/mailboxSelectors";
import {
  Mailbox,
  MailboxMap,
  MailboxResponseDto,
} from "../../../../types/api/mailbox";
import { buildMailboxAddress } from "../../../../utils/mailboxAddress";
import {
  Actions,
  AddressBox,
  AddressLabel,
  AddressLine,
  AddressText,
  AsideCol,
  Body,
  Card,
  CardCenterer,
  CopyIcon,
  InfoBadge,
  MainCol,
  SuiteNote,
  SuitePill,
  Subtitle,
  WhatToShipLink,
} from "./DemoAddressCard.styles";

interface DemoAddressCardProps {
  fullName: string | null;
  ffid: string | null;
  mailboxes: List<MailboxMap>;
  isLoading: boolean;
  getMailboxes: () => Promise<MailboxResponseDto>;
}

const DemoAddressCard: FC<DemoAddressCardProps> = ({
  fullName,
  ffid,
  mailboxes,
  isLoading,
  getMailboxes,
}) => {
  const { t } = useTranslation("common");
  const { branchId } = useParams();
  const navigate = useNavigate();

  // App prefetches mailboxes at sign-in, so this normally finds them already in
  // the store; it stays as the fallback for when that prefetch failed.
  // `isLoading` starts true and only settles once a request has finished, so
  // gating on it keeps this from racing a prefetch still in flight.
  useFetchOnceEffect(!mailboxes.size && !isLoading, getMailboxes);

  // Mailboxes carry the partner's shipping address (branch records hold a
  // generic warehouse one), so the CTA shows exactly what the mailbox page
  // does. A branch can have several mailboxes — the first one wins, matching
  // the order the mailbox page lists them in.
  const mailbox = mailboxes.find(
    (m) => String(m.getIn(["branch", "id"])) === branchId,
  );

  // authSelectFfid already carries the partner prefix (e.g. "SB1475"), the same
  // value the mailbox page shows — use it verbatim as the suite/box number.
  const suite = ffid || "";
  const suiteLine = suite ? t("mailbox.suite", { suite }) : "";

  // Same address model as the Mailboxes page, so both copy identical text.
  const address = mailbox
    ? buildMailboxAddress(mailbox.toJS() as Mailbox, fullName, suiteLine)
    : null;

  const addressLines = address
    ? [
        { text: address.name, suite: false },
        { text: address.street, suite: false },
        { text: address.suiteLine, suite: true },
        { text: address.cityLine, suite: false },
        { text: address.phone, suite: false },
      ].filter((line) => !!line.text)
    : [];

  const fullAddress = address?.fullAddress ?? "";
  const hasAddress = addressLines.length > 0;

  const handleCopyAll = () =>
    toast.success(t("common.copiedToClipboard"), {
      toastId: "demo-address-copied",
      icon: false,
    });

  return (
    <CardCenterer>
      <Card>
        <Body>
          <MainCol>
            <Subtitle>{t("parcels.demo.cta.subtitle")}</Subtitle>
            <Actions>
              {hasAddress && (
                <CopyToClipboardLib text={fullAddress} onCopy={handleCopyAll}>
                  <Button
                    variant={ButtonVariant.Filled}
                    color={ButtonColor.Primary}
                  >
                    {t("mailbox.copyAddress")}
                  </Button>
                </CopyToClipboardLib>
              )}
              <Button
                variant={
                  hasAddress ? ButtonVariant.Bordered : ButtonVariant.Filled
                }
                color={hasAddress ? ButtonColor.Neutral : ButtonColor.Primary}
                onClick={() => navigate("/parcels/expected")}
              >
                {t("parcels.demo.cta.declareExpected")}
              </Button>
              <WhatToShipLink to="/terms">
                {t("parcels.demo.cta.whatToShip")} →
              </WhatToShipLink>
            </Actions>
          </MainCol>
          {hasAddress && (
            <AsideCol>
              <AddressBox>
                <AddressLabel>
                  {t("parcels.demo.cta.addressLabel", {
                    country: mailbox?.get("country"),
                  })}
                </AddressLabel>
                {addressLines.map((line) => (
                  <AddressLine key={line.text}>
                    {line.suite ? (
                      <SuitePill>{line.text}</SuitePill>
                    ) : (
                      <AddressText>{line.text}</AddressText>
                    )}
                    <CopyToClipboard text={line.text}>
                      <CopyIcon type={IconType.Copy} />
                    </CopyToClipboard>
                  </AddressLine>
                ))}
              </AddressBox>
              {!!suite && (
                <SuiteNote>
                  <InfoBadge>i</InfoBadge>
                  <span>{t("mailbox.suiteNote", { suite })}</span>
                </SuiteNote>
              )}
            </AsideCol>
          )}
        </Body>
      </Card>
    </CardCenterer>
  );
};

const withConnect = connect(
  createStructuredSelector({
    fullName: authSelectFullName,
    ffid: authSelectFfid,
    mailboxes: mailboxSelectMailboxes,
    isLoading: mailboxSelectLoading,
  }),
  { getMailboxes },
);

export default withConnect(DemoAddressCard);
